import React, { useEffect, useState } from "react";
import "./preference.css";
import PreferenceApi from "../../../api/PreferenceApi";
import {
  EPreferencePropertyType,
  PreferenceFile,
  PreferenceProperty,
  PreferenceValue,
} from "../../../Model/Preference";
import PreferenceItem from "./PreferenceItem";
import PreferenceWrapper from "./PreferenceWrapper";
import PreferenceCategory from "./PreferenceCategory";
import PreferenceTitle from "./PreferenceTitle";
import PreferenceSearch from "./PreferenceSearch";
import { useDispatch } from "react-redux";
import { sendNotification } from "../../../redux/actions/NotificationAction";
import { ENotificationType } from "../../../Model/ENotificationType";
import LayoutContent from "../../LayoutContent";
import Header from "../../Header/Header";
import Breadcrumb from "../../Header/Breadcrumb";
import MtiButton from "../../Buttons/MtiButton";
import SettingsNavSubMenu from "../SettingsNavSubMenu";

const getMaxIndexMultiValues = (
  values: PreferenceValue[],
  path: string,
  isLastIndex = false
) => {
  const backIndex = isLastIndex ? 1 : 2;
  const testRegex = isLastIndex
    ? new RegExp(`${path}.\\d+`)
    : new RegExp(`${path}.\\d+.`);
  let maxIndex = 0;
  values.forEach((prefVal) => {
    const keyCollection = prefVal.preferenceKey.match(testRegex);
    if (keyCollection) {
      const splittedKeyCollection = keyCollection[0].split(".");
      const index =
        splittedKeyCollection[splittedKeyCollection.length - backIndex];
      const indexNum = parseInt(index);
      if (indexNum > maxIndex) {
        maxIndex = indexNum;
      }
    }
  });
  return maxIndex;
};

type Props = {
  setOnApply: React.Dispatch<
    React.SetStateAction<{
      onApply: () => void;
      disabled: boolean;
    }>
  >;
  setOnCancel: React.Dispatch<
    React.SetStateAction<{
      onCancel: () => void;
      disabled: boolean;
    }>
  >;
};

type expandType = {
  propsPath: string;
  isExpand: boolean;
};

export default function SettingsPreferenceView({
  setOnApply,
  setOnCancel,
}: Props) {
  const [propertyComponents, setPropertyComponents] = useState(
    [] as React.ReactNode[]
  );
  const [preferenceFiles, setPreferenceFiles] = useState(
    [] as PreferenceFile[]
  );
  const [updatedPreferenceValues, setUpdatedPreferenceValues] = useState(
    [] as PreferenceValue[]
  );
  const [selectedCategory, setSelectedCategory] = useState("");
  const [searchActive, setSearchActive] = useState(false);
  const [itemsExpandState, setItemsExpandState] = useState([] as expandType[]);
  const [isCollapsedAll, setisCollapsedAll] = useState(false);

  const dispatch = useDispatch();
  const selectedFiles = preferenceFiles.filter(
    (file) => file.name === selectedCategory
  );
  let selectedFile = undefined;
  if (selectedFiles.length > 0) {
    selectedFile = selectedFiles[0];
  }

  const onUpdateValues = (
    key: string,
    values: string[],
    isMultiValue = false
  ) => {
    // Update Main State for Front End
    const fileName = key.split(".")[0];
    const prefFileIndex = preferenceFiles.findIndex(
      (prefFile) => prefFile.name === fileName
    );
    let prefFiles = [...preferenceFiles];
    let prefFile = prefFiles[prefFileIndex];
    let prefValues = prefFile.values;
    const maxIndex = getMaxIndexMultiValues(prefValues, key, true);
    if (isMultiValue) {
      for (let i = 0; i <= maxIndex; i++) {
        let keyUsed = `${key}.${i}`;
        const valIndex = prefValues.findIndex(
          (prefVal) => prefVal.preferenceKey === keyUsed
        );
        if (valIndex > -1) {
          prefValues.splice(valIndex, 1);
        }
      }
      for (let j = 0; j < values.length; j++) {
        let keyUsed = `${key}.${j}`;
        prefValues.push({
          preferenceKey: keyUsed,
          preferenceValue: values[j],
        });
      }
    } else {
      const valIndex = prefValues.findIndex(
        (prefVal) => prefVal.preferenceKey === key
      );
      if (valIndex > -1) {
        prefValues.splice(valIndex, 1);
      }
      prefValues.push({
        preferenceKey: key,
        preferenceValue: values[0],
      });
    }

    prefFile.values = [...prefValues];
    setPreferenceFiles([...prefFiles]);

    // Update values to be sent to backend
    let updatedValues = [...updatedPreferenceValues];
    if (isMultiValue) {
      for (let i = 0; i <= maxIndex; i++) {
        const keyUsed = `${key}.${i}`;
        const updatedValIndex = updatedValues.findIndex(
          (updateVal) => updateVal.preferenceKey === keyUsed
        );
        if (updatedValIndex > -1) {
          updatedValues.splice(updatedValIndex, 1);
        }
      }
      for (let j = 0; j < values.length; j++) {
        const keyUsed = `${key}.${j}`;
        updatedValues.push({
          preferenceKey: keyUsed,
          preferenceValue: values[j],
        });
      }
    } else {
      const updatedValIndex = updatedValues.findIndex(
        (updateVal) => updateVal.preferenceKey === key
      );
      if (updatedValIndex > -1) {
        updatedValues.splice(updatedValIndex, 1);
      }
      updatedValues.push({
        preferenceKey: key,
        preferenceValue: values[0],
      });
    }
    setUpdatedPreferenceValues([...updatedValues]);
  };

  const onChangeExpandState = (key: string, value: boolean) => {
    let newExpandState = [...itemsExpandState];
    const indexVal = itemsExpandState.findIndex(
      (state) => state.propsPath === key
    );
    if (indexVal != -1) {
      newExpandState[indexVal] = {
        propsPath: key,
        isExpand: value,
      };
    }
    setItemsExpandState(newExpandState);
  };

  const buildPropertiesComponent = (
    property: PreferenceProperty,
    values: PreferenceValue[],
    components: React.ReactNode[],
    path: string = "",
    level = 0
  ) => {
    level++;
    if (property.children == null) {
      components.push(
        <PreferenceItem
          propertyPath={path}
          preferenceProperty={property}
          key={`${path}.${property.propertyName}`}
          isStandAlone={level <= 1}
          preferenceValues={values}
          onChangeValues={onUpdateValues}
        />
      );
    } else {
      const currentPath = `${path}.${property.propertyName}`;
      const filteredValues = values.filter((prefVal) =>
        prefVal.preferenceKey.includes(currentPath)
      );
      if (property.isMultiValue) {
        const maxIndex = getMaxIndexMultiValues(filteredValues, currentPath);
        for (let i = 0; i <= maxIndex; i++) {
          let children = [] as React.ReactNode[];
          const expandIndex = itemsExpandState.findIndex(
            (state) => state.propsPath === `${currentPath}.${i}`
          );
          const expand = itemsExpandState[expandIndex];
          let expandState = expand ? expand.isExpand : false;
          property.children.forEach((value) =>
            buildPropertiesComponent(
              value,
              filteredValues,
              children,
              `${currentPath}.${i}`,
              level
            )
          );
          components.push(
            <PreferenceWrapper
              name={`${currentPath}.${i}`}
              displayName={`${property.displayName} ${i}`}
              withExpand={level <= 1}
              isExpand={expandState}
              key={`${currentPath}.${i}`}
              setExpandState={onChangeExpandState}
            >
              {children}
            </PreferenceWrapper>
          );
        }
      } else {
        let children = [] as React.ReactNode[];
        const expandIndex = itemsExpandState.findIndex(
          (state) => state.propsPath === currentPath
        );
        const expand = itemsExpandState[expandIndex];
        let expandState = expand ? expand.isExpand : false;
        property.children.forEach((value) =>
          buildPropertiesComponent(
            value,
            filteredValues,
            children,
            currentPath,
            level
          )
        );
        components.push(
          <PreferenceWrapper
            name={currentPath}
            displayName={property.displayName}
            withExpand={level <= 1}
            isExpand={expandState}
            key={`${currentPath}`}
            setExpandState={onChangeExpandState}
          >
            {children}
          </PreferenceWrapper>
        );
      }
    }
  };

  const getAllExpandState = (
    property: PreferenceProperty,
    values: PreferenceValue[],
    path: string = "",
    result: expandType[] = []
  ) => {
    const newPath = `${path}.${property.propertyName}`;
    if (property.propertyType === EPreferencePropertyType.PreferenceClass) {
      if (property.isMultiValue) {
        const valueMaxIndex = getMaxIndexMultiValues(values, newPath, false);
        for (let i = 0; i <= valueMaxIndex; i++) {
          result.push({
            propsPath: `${newPath}.${i}`,
            isExpand: false,
          });
        }
      } else {
        result.push({
          propsPath: newPath,
          isExpand: false,
        });
      }
    }
  };

  useEffect(() => {
    let allState: boolean[] = [];
    itemsExpandState
      .filter((state) => state.propsPath.includes(selectedCategory))
      .forEach((state) => {
        allState.push(state.isExpand);
      });
    if (allState.some((test) => !test)) {
      setisCollapsedAll(true);
    }
    if (allState.every((test) => test)) {
      setisCollapsedAll(false);
    }
  }, [itemsExpandState, selectedCategory]);

  useEffect(() => {
    PreferenceApi.getPreferences().then((values) => {
      let selectedFile = values[0].name;
      let ExpandState: expandType[] = [];
      setSelectedCategory(selectedFile);
      setPreferenceFiles(values);
      values.forEach((prefFiles) => {
        prefFiles.properties.forEach((prefProps) => {
          getAllExpandState(
            prefProps,
            prefFiles.values,
            prefFiles.name,
            ExpandState
          );
        });
      });
      setItemsExpandState(ExpandState);
    });
  }, []);

  useEffect(() => {
    let mainComponents = [] as React.ReactNode[];
    preferenceFiles.forEach((prefFiles) => {
      if (prefFiles.name === selectedCategory) {
        prefFiles.properties.forEach((prefProps) => {
          buildPropertiesComponent(
            prefProps,
            prefFiles.values,
            mainComponents,
            prefFiles.name
          );
        });
      }
    });
    setPropertyComponents(mainComponents);
  }, [preferenceFiles, selectedCategory, itemsExpandState]);

  const onCollapseExpandAll = () => {
    let newExpandState = [...itemsExpandState];
    newExpandState.forEach((state) => {
      if (state.propsPath.includes(selectedCategory)) {
        if (isCollapsedAll) {
          state.isExpand = true;
        } else {
          state.isExpand = false;
        }
      }
    });
    setItemsExpandState(newExpandState);
  };

  const onCancel = () => {
    PreferenceApi.getPreferences().then((values) => {
      setPreferenceFiles(values);
      setUpdatedPreferenceValues([]);
    });
  };

  const onApply = () => {
    PreferenceApi.setPreferences(updatedPreferenceValues).then((result) => {
      setUpdatedPreferenceValues([]);
      dispatch(
        sendNotification(
          ENotificationType.Success,
          "Your Changes has been saved successfully"
        )
      );
    });
  };

  return (
    <LayoutContent>
      <LayoutContent.Header>
        <Header>
          <Breadcrumb
            items={[
              {
                title: "Preferences",
                to: "/settings/preferences",
                disabled: false,
              },
            ]}
          />
          <Header.RightSide>
            <MtiButton
              size="large"
              variant="secondary"
              style={{
                margin: "auto 10px auto 25px",
              }}
              disabled={updatedPreferenceValues.length < 1}
              onClick={onCancel}
            >
              CANCEL
            </MtiButton>
            <MtiButton
              size="large"
              disabled={updatedPreferenceValues.length < 1}
              style={{
                margin: "auto 16px auto 10px",
              }}
              onClick={onApply}
            >
              APPLY
            </MtiButton>
          </Header.RightSide>
        </Header>
      </LayoutContent.Header>
      <LayoutContent.SubMenu>
        <SettingsNavSubMenu />
      </LayoutContent.SubMenu>
      <LayoutContent.Content
        style={{ height: "100%", paddingLeft: 16, paddingRight: 16 }}
      >
        <div className="preference-layout">
          {/* <div
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "stretch",
              marginRight: "36px",
              marginTop: "24px",
              width: "275px",
            }}
          >
            {/* <PreferenceSearch /> }
            
          </div> */}
          <PreferenceCategory
            preferenceFiles={preferenceFiles}
            selectedCategory={selectedCategory}
            setSelectedCategory={setSelectedCategory}
          />
          <div className="property-container">
            <PreferenceTitle
              title={selectedFile ? selectedFile.displayName : ""}
              onCollapseExpandItem={onCollapseExpandAll}
              isAllCollapsed={isCollapsedAll}
              showCollapseExpand={
                itemsExpandState.filter((state) =>
                  state.propsPath.includes(selectedCategory)
                ).length > 0
              }
            />
            <div style={{ overflowY: "auto" }}>
              <div className="preference-main">{propertyComponents}</div>
            </div>
          </div>
        </div>
      </LayoutContent.Content>
    </LayoutContent>
  );
}
